/**
 * When the board is redrawn sharp after zooming.
 *
 * The world layer is its own composited layer (translate3d). While a zoom is under way it also
 * carries `will-change: transform`, which tells the browser to keep the bitmap it has and only
 * scale it: smooth, but soft when zooming in. Chrome never redraws such a layer at a new scale
 * on its own (https://developer.chrome.com/blog/re-rastering-composite), which is how items
 * stayed blurred, even across reloads. Dropping `will-change` once the zoom settles makes the
 * browser draw the layer again at the zoom it is shown at; panning never needs a redraw.
 *
 * Redrawing costs the browser a raster of everything on screen, so the per-device setting
 * (`toolSettings.render`) decides when it is worth it.
 */
export type RenderMode = 'sharp' | 'balanced' | 'performance';

export const RENDER_MODES: { id: RenderMode; label: string }[] = [
  { id: 'sharp', label: 'Sharp' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'performance', label: 'Performance' },
];

export const isRenderMode = (v: unknown): v is RenderMode => RENDER_MODES.some((m) => m.id === v);

/** Zoom from which Balanced always redraws. */
export const SHARP_FROM_ZOOM = 0.75;

/** Quiet time after the last zoom change before the layer is redrawn. */
export const REDRAW_AFTER_MS = 200;

/**
 * Whether to redraw at `zoom` a layer last drawn at `drawnAt` (NaN: never drawn at a known zoom).
 * Balanced redraws from 75 % up, and below that once the bitmap is blown up by more than half
 * again; Performance only once it is more than 2.5 times too small. Both also redraw when it is
 * over four times too large, which only costs memory.
 */
export function shouldRedraw(mode: RenderMode, zoom: number, drawnAt: number): boolean {
  if (!(drawnAt > 0)) return true;
  if (zoom === drawnAt) return false;
  const ratio = zoom / drawnAt;
  switch (mode) {
    case 'sharp':
      return true;
    case 'balanced':
      return zoom >= SHARP_FROM_ZOOM || ratio > 1.5 || ratio < 0.25;
    case 'performance':
      return ratio > 2.5 || ratio < 0.25;
  }
}
